import 'govuk-frontend/dist/govuk/govuk-frontend.min.css'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { GovUKTypography } from './GovUKTypography'
import { GovUKButton } from './GovUKButton'
import { GovUKPageContainer } from './GovUKPageContainer'
import { GovUKPageHeader } from './GovUKPageHeader'
import { GovUKGrid, GovUKGridColumn } from './GovUKGrid'
import { GovUKLink } from './GovUKLink'

interface GovUKResourceListPageProps<T extends { id: string }> {
  title: string
  caption?: string
  addRoute: string
  addButtonText?: string
  detailRoute: (id: string) => string
  items: T[]
  isLoading: boolean
  queryKey: readonly unknown[]
  deleteFn: (input: { data: { id: string } }) => Promise<{ success: boolean }>
  renderItem: (item: T) => {
    title: string
    subtitle?: string
  }
  emptyMessage?: string
  deleteConfirmMessage?: (item: T) => string
}

export function GovUKResourceListPage<T extends { id: string }>({
  title,
  caption,
  addRoute,
  addButtonText = `Add ${title.toLowerCase()}`,
  detailRoute,
  items,
  isLoading,
  queryKey,
  deleteFn,
  renderItem,
  emptyMessage = `No ${title.toLowerCase()} found`,
  deleteConfirmMessage = () =>
    `Are you sure you want to delete this ${title.toLowerCase()}?`,
}: GovUKResourceListPageProps<T>) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  if (isLoading) {
    return (
      <GovUKPageContainer>
        <GovUKTypography variant="body">
          Loading {title.toLowerCase()}...
        </GovUKTypography>
      </GovUKPageContainer>
    )
  }

  const handleDelete = async (item: T) => {
    if (!window.confirm(deleteConfirmMessage(item))) return

    try {
      await deleteFn({ data: { id: item.id } })
      await queryClient.invalidateQueries({ queryKey })
      await queryClient.refetchQueries({ queryKey })
    } catch (error) {
      alert(`Failed to delete ${title.toLowerCase()}.`)
    }
  }

  return (
    <GovUKPageContainer>
      <GovUKPageHeader title={title} caption={caption}>
        <Link to={addRoute}>
          <GovUKButton>{addButtonText}</GovUKButton>
        </Link>
      </GovUKPageHeader>

      {items.length === 0 ? (
        <GovUKTypography variant="body">{emptyMessage}</GovUKTypography>
      ) : (
        <GovUKGrid>
          <GovUKGridColumn width="full">
            {items.map((item) => {
              const { title: itemTitle, subtitle } = renderItem(item)
              return (
                <div
                  key={item.id}
                  className="govuk-!-margin-bottom-3"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <GovUKTypography variant="heading-m" marginBottom={1}>
                      <GovUKLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          navigate({ to: detailRoute(item.id) as any })
                        }}
                        style={{ textDecoration: 'none' }}
                      >
                        {itemTitle}
                      </GovUKLink>
                    </GovUKTypography>
                    {subtitle && (
                      <GovUKTypography
                        variant="body-s"
                        marginBottom={0}
                        style={{ color: '#505a5f' }}
                      >
                        {subtitle}
                      </GovUKTypography>
                    )}
                  </div>
                  <GovUKButton
                    variant="warning"
                    style={{ marginLeft: 16 }}
                    onClick={() => handleDelete(item)}
                  >
                    Delete
                  </GovUKButton>
                </div>
              )
            })}
          </GovUKGridColumn>
        </GovUKGrid>
      )}
    </GovUKPageContainer>
  )
}
